import type { JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Tour } from 'antd';
import type { TourProps } from 'antd';
import { CloseOutlined, PlayCircleOutlined, RightOutlined } from '@ant-design/icons';

/**
 * Онбординг УК на главной (прототип FISHKA HUB): тёмная карточка «FISHKA HUB
 * за 5 минут» с коротким туром по интерфейсу и переходом в обучение.
 *
 * Тур — antd Tour: шаги цепляются к реальным элементам оболочки по
 * `data-tour` (Layout.tsx: search / create / user, HUB 2.0). Если элемента нет (узкий экран, свёрнутое
 * меню) — шаг показывается по центру, а не ломает тур.
 *
 * Пройден/скрыт — в localStorage: это подсказка для конкретного человека на
 * его устройстве, серверу о ней знать незачем.
 */
const DONE_KEY = 'fk.onboarding.done';

/**
 * Тур уже пройден? HomePage спрашивает ДО рендера: без этого карточка
 * возвращает null, а её место в сетке остаётся — на главной зияла пустая
 * колонка в две трети ширины (владелец 2026-09-04, после прохождения тура).
 */
export function isOnboardingDone(): boolean {
  return readDone();
}

function readDone(): boolean {
  try {
    return localStorage.getItem(DONE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Цель шага тура. antd не принимает `() => HTMLElement | null`, поэтому
 * возвращаем либо функцию с найденным элементом, либо undefined — тогда шаг
 * показывается по центру экрана, а не ломает тур.
 */
function target(selector: string): (() => HTMLElement) | undefined {
  const el = typeof document === 'undefined' ? null : document.querySelector<HTMLElement>(selector);
  return el ? () => el : undefined;
}

export function OnboardingHero(): JSX.Element | null {
  const [done, setDone] = useState(readDone);
  const [tourOpen, setTourOpen] = useState(false);

  const finish = (): void => {
    setTourOpen(false);
    setDone(true);
    try {
      localStorage.setItem(DONE_KEY, '1');
    } catch {
      /* приватный режим — просто покажем карточку снова */
    }
  };

  const steps: TourProps['steps'] = [
    {
      title: 'FISHKA HUB за 5 минут',
      description:
        'Это центр знаний, обучения и стандартов сети. Здесь статьи и регламенты, курсы для сотрудников и контроль актуальности базы.',
    },
    {
      title: 'Меню слева',
      description:
        'Разделы сгруппированы: «Для меня», «Знания», «Обучение» и «Управление». Счётчики у пунктов — то, что ждёт действия: ответы учеников, просроченные статьи, обращения.',
      target: target('.fk-sidebar'),
      placement: 'right',
    },
    {
      title: 'Актуальность базы',
      description:
        'У каждой статьи есть владелец и срок перепроверки. Здесь видно, что просрочено и у чего нет владельца; владельцу напоминание уходит за 7 дней до срока.',
      target: target('a[href="/admin/freshness"]'),
      placement: 'right',
    },
    {
      title: 'Проверка ответов',
      description: 'Открытые ответы и фото из тестов приходят сюда на ручную проверку. Счётчик показывает очередь.',
      target: target('a[href="/admin/reviews"]'),
      placement: 'right',
    },
    {
      title: 'Поиск',
      description: 'Поиск по статьям, курсам, стандартам, файлам и разделам. Открывается и сочетанием Ctrl+K (⌘K на Mac) из любого места.',
      target: target('[data-tour="search"]'),
      placement: 'bottom',
    },
    {
      title: 'Создать',
      description:
        'Новая статья или курс, а также «Написать в УК»: ошибка, улучшение или обращение. Обращения попадают в CRM → Обращения.',
      target: target('[data-tour="create"]'),
      placement: 'bottom',
    },
    {
      title: 'Меню пользователя',
      description: 'Профиль, мои очки и бейджи, уведомления, тема оформления и кнопка «В CRM» — вернуться в БРАСОЛ.',
      target: target('[data-tour="user"]'),
      placement: 'bottomRight',
    },
  ];

  if (done) return null;

  return (
    <>
      <div className="fk-onb">
        {/* Ролл с белым рисом (владелец 14.10.2026): у maki-character.png рис прозрачный и на тёмном
            баннере выглядел фиолетовым; обложки курсов по-прежнему берут исходный файл. */}
        <img src="/brand/illustrations/maki-character-filled.png" alt="" className="fk-onb-img" />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <span className="fk-onb-badge">Знакомство с FISHKA HUB</span>
          <h2 className="fk-onb-title">FISHKA HUB за 5 минут</h2>
          <p className="fk-onb-text">
            Короткий тур по интерфейсу: как создавать статьи, держать базу актуальной и собирать курсы.
            Потом — курсы в разделе «Обучение».
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="fk-onb-cta" onClick={() => setTourOpen(true)}>
              <PlayCircleOutlined /> Начать тур
            </button>
            <Link to="/education" className="fk-onb-ghost">
              Открыть обучение <RightOutlined style={{ fontSize: 11 }} />
            </Link>
          </div>
        </div>
        <button type="button" className="fk-onb-close" onClick={finish} title="Больше не показывать" aria-label="Скрыть">
          <CloseOutlined />
        </button>
        <style>{`
          .fk-onb{position:relative;display:flex;align-items:center;gap:22px;padding:22px 24px;border-radius:var(--fk-radius-card);background:linear-gradient(160deg,#2A2342,#1C1830);color:#fff;overflow:hidden}
          .fk-onb-img{height:120px;flex-shrink:0;opacity:.95}
          .fk-onb-badge{display:inline-flex;padding:3px 10px;border-radius:var(--fk-radius-sm);background:var(--fk-orange);font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.05em}
          .fk-onb-title{font-size:24px;font-weight:800;margin:10px 0 6px}
          .fk-onb-text{font-size:14px;line-height:1.55;opacity:.85;margin:0 0 14px;max-width:560px}
          .fk-onb-cta{display:inline-flex;align-items:center;gap:8px;padding:10px 18px;border-radius:var(--fk-radius-md);border:none;background:var(--fk-orange);color:#fff;font-family:inherit;font-weight:600;font-size:14px;cursor:pointer}
          .fk-onb-cta:hover{filter:brightness(1.06)}
          .fk-onb-ghost{display:inline-flex;align-items:center;gap:8px;padding:10px 18px;border-radius:var(--fk-radius-md);border:1px solid rgba(255,255,255,.28);color:#fff;font-weight:600;font-size:14px}
          .fk-onb-ghost:hover{color:#fff;background:rgba(255,255,255,.10)}
          .fk-onb-close{position:absolute;top:12px;right:12px;width:28px;height:28px;display:grid;place-items:center;border-radius:var(--fk-radius-md);border:none;background:rgba(255,255,255,.10);color:#fff;cursor:pointer;font-size:12px}
          .fk-onb-close:hover{background:rgba(255,255,255,.18)}
          @media (max-width: 720px){ .fk-onb-img{display:none} }
        `}</style>
      </div>
      <Tour open={tourOpen} onClose={finish} onFinish={finish} steps={steps} />
    </>
  );
}
